import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Badge, Button, Spinner, Divider, useColorModeValue, useToast, useDisclosure,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  FormControl, Input,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import Card from '../../components/card/Card';
interface AdminUserDetail {
  id: string;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Renamed from supabaseUserId post-Zitadel migration. Backend now sends
  // idpSubject (admin.admin_users.idp_subject column).
  idpSubject: string;
}

function roleBadgeColor(role: string): string {
  if (role === 'system_admin') return 'brand';
  if (role === 'content_curator') return 'blue';
  return 'green';
}

export function AdminUserDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => apiClient.get<AdminUserDetail>(`/admin/admin-users/${id}`),
    enabled: Boolean(id),
  });

  const deactivate = useMutation({
    mutationFn: () => apiClient.post(`/admin/admin-users/${id}/deactivate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      toast({ title: t('admin.users.deactivateSuccess'), status: 'success', duration: 3000 });
    },
    onError: () => {
      toast({ title: t('admin.users.deactivateError'), status: 'error', duration: 5000 });
    },
  });

  const reactivate = useMutation({
    mutationFn: () => apiClient.post(`/admin/admin-users/${id}/reactivate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      toast({ title: t('admin.users.reactivateSuccess'), status: 'success', duration: 3000 });
    },
    onError: () => {
      toast({ title: t('admin.users.reactivateError'), status: 'error', duration: 5000 });
    },
  });

  const resetMfa = useMutation({
    mutationFn: () => apiClient.post(`/admin/admin-users/${id}/reset-mfa`),
    onSuccess: () => {
      toast({ title: t('admin.users.resetMfaSuccess'), status: 'success', duration: 3000 });
    },
    onError: () => {
      toast({ title: t('admin.users.resetMfaError'), status: 'error', duration: 5000 });
    },
  });

  const hardDelete = useMutation({
    mutationFn: () => apiClient.delete(`/admin/admin-users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: t('admin.users.hardDelete.successToast'), status: 'success', duration: 3000 });
      navigate('/admin-users');
    },
    onError: () => {
      toast({ title: t('admin.users.hardDelete.errorToast'), status: 'error', duration: 5000 });
    },
  });

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  if (!user) return <Text color={textColor}>{t('admin.common.noResults')}</Text>;

  const roleKey = user.role === 'content_curator' ? 'contentCurator' : user.role === 'system_admin' ? 'systemAdmin' : 'support';
  const isDeleteConfirmMatch = deleteConfirmText === user.email;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-user-detail-page">
      <Card p="20px" mb="20px">
        <Flex gap={6} flexWrap="wrap">
          <Box flex={1} minW="200px">
            <Text color={secondaryTextColor} fontSize="sm" mb="4px">{t('admin.users.email')}</Text>
            <Text color={textColor} fontWeight="500" data-testid="user-email">{user.email}</Text>
          </Box>
          <Box flex={1} minW="200px">
            <Text color={secondaryTextColor} fontSize="sm" mb="4px">{t('admin.users.role')}</Text>
            <Badge
              colorScheme={roleBadgeColor(user.role)}
              borderRadius="8px"
              px="10px"
              data-testid="user-role"
            >
              {t(`admin.users.roles.${roleKey}`)}
            </Badge>
          </Box>
        </Flex>
        <Flex gap={6} mt={4} flexWrap="wrap">
          <Box flex={1} minW="200px">
            <Text color={secondaryTextColor} fontSize="sm" mb="4px">{t('admin.common.status')}</Text>
            <Badge
              colorScheme={user.isActive ? 'green' : 'red'}
              borderRadius="8px"
              px="10px"
              data-testid="user-status"
            >
              {user.isActive ? t('admin.common.active') : t('admin.common.inactive')}
            </Badge>
          </Box>
          <Box flex={1} minW="200px">
            <Text color={secondaryTextColor} fontSize="sm" mb="4px">{t('admin.users.lastLogin')}</Text>
            <Text color={textColor} fontWeight="500">
              {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '-'}
            </Text>
          </Box>
        </Flex>
        <Flex gap={6} mt={4}>
          <Box flex={1}>
            <Text color={secondaryTextColor} fontSize="sm" mb="4px">{t('admin.users.created')}</Text>
            <Text color={textColor} fontWeight="500">
              {new Date(user.createdAt).toLocaleString()}
            </Text>
          </Box>
        </Flex>
      </Card>

      <Card p="20px">
        <Text fontSize="lg" fontWeight="700" color={textColor} mb="16px">
          {t('admin.common.actions')}
        </Text>
        <Divider mb="16px" />
        <Flex gap={3} flexWrap="wrap">
          <Button
            variant="brand"
            onClick={() => navigate(`/admin-users/${id}/edit`)}
            data-testid="btn-edit"
          >
            {t('common.edit')}
          </Button>
          {user.isActive ? (
            <Button
              colorScheme="red"
              onClick={() => deactivate.mutate()}
              isDisabled={deactivate.isPending}
              data-testid="btn-deactivate"
            >
              {t('admin.users.deactivate')}
            </Button>
          ) : (
            <Button
              colorScheme="green"
              onClick={() => reactivate.mutate()}
              isDisabled={reactivate.isPending}
              data-testid="btn-reactivate"
            >
              {t('admin.users.reactivate')}
            </Button>
          )}
          <Button
            variant="brand"
            onClick={() => resetMfa.mutate()}
            isDisabled={resetMfa.isPending}
            data-testid="btn-reset-mfa"
          >
            {t('admin.users.resetMfa')}
          </Button>
          <Button
            colorScheme="red"
            variant="outline"
            onClick={deleteDialog.onOpen}
            data-testid="btn-hard-delete"
          >
            {t('admin.users.hardDelete.button')}
          </Button>
        </Flex>
      </Card>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          if (!hardDelete.isPending) {
            setDeleteConfirmText('');
            deleteDialog.onClose();
          }
        }}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader pb="8px">
            <Text fontSize="lg" fontWeight="700" color={textColor}>
              {t('admin.users.hardDelete.dialogTitle')}
            </Text>
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text color={secondaryTextColor} mb={4}>
              {t('admin.users.hardDelete.warning')}
            </Text>
            <Text color={textColor} fontSize="sm" fontWeight="600" mb={2}>
              {t('admin.users.hardDelete.confirmLabel', { email: user.email })}
            </Text>
            <FormControl>
              <Input
                size="sm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                data-testid="hard-delete-confirm-input"
              />
            </FormControl>
          </AlertDialogBody>

          <AlertDialogFooter gap={2}>
            <Button
              ref={cancelRef}
              onClick={() => {
                setDeleteConfirmText('');
                deleteDialog.onClose();
              }}
              isDisabled={hardDelete.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={() => hardDelete.mutate()}
              isDisabled={!isDeleteConfirmMatch || hardDelete.isPending}
              isLoading={hardDelete.isPending}
              data-testid="hard-delete-confirm-button"
            >
              {t('admin.users.hardDelete.confirmButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
}
